// @ts-expect-error moduleResolution:nodenext issue 54523
import { html } from 'lit';
// @ts-expect-error moduleResolution:nodenext issue 54523
import { customElement, state } from 'lit/decorators.js';
import LightElement from './light-element.js';
import './toast-notification.ts';
import Helpers from '../../typescript/modules/helpers.js';
import Logger from '../../typescript/modules/logger.js';
import Tracking from '../../typescript/modules/tracking.js';

declare var Splide: any;

@customElement('product-comparison-bar')
export default class ProductComparisonBar extends LightElement {

	@state()
	productIds: string[] = [];

	private productComparisonIdsKey: string = 'productComparisonIds';

	@state()
	products: any[] = [];

	@state()
	showComparisonBar: boolean = false;

	@state()
	comparingMode: boolean;

	@state()
	lastProductIds: string[] = [];

	@state()
	maxProducts: number = 5;

	private splideInstance: any = null;

	private get validProducts() {
		return this.products.filter(product => product && product.Product);
	}

	connectedCallback() {
		super.connectedCallback();

		if (!this.shouldShowBarOnPage()) {
			this.comparingMode = false;
			return;
		}

		this.loadProductIds();
		this.checkIsComparingMode();
		setTimeout(() => {
			this.updateToggleStates();
		}, 1000);
		document.addEventListener('productComparisonProductUpdate', this.handleUpdateProduct.bind(this));
		document.addEventListener('productComparisonMode', this.handleComparisonModeChange.bind(this));
	}

	private shouldShowBarOnPage() {
		if (document.querySelector('.mRange')) {
			return true;
		}
		return false;
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		document.removeEventListener('productComparisonProductUpdate', this.handleUpdateProduct.bind(this));
		document.removeEventListener('productComparisonMode', this.handleComparisonModeChange.bind(this));

		if (this.splideInstance) {
			this.splideInstance.destroy();
			this.splideInstance = null;
		}

		this.setLiveChatHidden(false);
		window.removeEventListener('resize', this.updateLiveChatVisibility);
	}

	private static readonly liveChatOverlapMaxWidth = 1225;

	private updateLiveChatVisibility = () => {
		this.setLiveChatHidden(this.showComparisonBar && window.innerWidth < ProductComparisonBar.liveChatOverlapMaxWidth);
	};

	updated(changedProperties: Map<string, unknown>) {
		if (changedProperties.has('showComparisonBar')) {
			this.updateLiveChatVisibility();
			if (this.showComparisonBar) {
				window.addEventListener('resize', this.updateLiveChatVisibility);
			} else {
				window.removeEventListener('resize', this.updateLiveChatVisibility);
			}
		}
	}

	private setLiveChatHidden(hidden: boolean) {
		const liveChatFacade = document.querySelector('.c-live-chat-facade');
		if (!liveChatFacade) {
			return;
		}
		if (hidden) {
			liveChatFacade.setAttribute('hidden', '');
		} else {
			liveChatFacade.removeAttribute('hidden');
		}
	}

	async fetchProductDetails() {
		const invalidProductIds: string[] = [];
		
		const productPromises = this.productIds.map(async (productId) => {
			const product = await this.fetchSingleProduct(productId);
			
			if (!product || !product.Product || !product.Product.Id || !product.Product.DisplayName) {
				invalidProductIds.push(productId);
				return null;
			}
			
			return product;
		});

		const results = await Promise.all(productPromises);
		
		this.products = results.filter(product => product !== null);

		if (invalidProductIds.length > 0) {
			this.removeInvalidProductsFromStorage(invalidProductIds);
		}
		
		this.shouldShowComparisonBar();
		await this.updateComplete;
	}

	private removeInvalidProductsFromStorage(invalidProductIds: string[]) {
		const comparisonKey = this.productComparisonIdsKey;
		let comparisonIds = JSON.parse(localStorage.getItem(comparisonKey) || '[]');
		comparisonIds = comparisonIds.filter((id: string) => !invalidProductIds.includes(id));
		localStorage.setItem(comparisonKey, JSON.stringify(comparisonIds));
		this.productIds = comparisonIds;
		this.updateToggleStates();
		this.requestUpdate();
		
		const updatedEvent = Helpers.createCustomEventForDispatch('productComparisonUpdated');
		document.dispatchEvent(updatedEvent);
	}

	async fetchSingleProduct(productId: string) {
		try {
			const response = await fetch(`/api/productviewer/getproduct/?id=${productId}&quantity=1`);
			if (!response.ok) {
				throw new Error(`Failed to fetch product with ID: ${productId}`);
			}
			return await response.json();
		} catch (error) {
			Logger.trackException(error, `Error fetching product ${productId} for comparison bar`);
			return null;
		}
	}

	async addProduct(productId: string) {
		const product = await this.fetchSingleProduct(productId);
		if (product) {
			this.products = [...this.products, product];
			this.shouldShowComparisonBar();
			await this.updateComplete;
		}
	}

	async removeProduct(productId: string) {
		this.products = this.products.filter(product => product.Product.Id !== productId);
		this.shouldShowComparisonBar();
		await this.updateComplete;
	}

	async handleRemoveProduct(productId: string) {
		const productToRemove = this.products.find(product => product.Product.Id === productId);
		const productName = productToRemove?.Product.DisplayName;

		this.productIds = this.productIds.filter(id => id !== productId);

		localStorage.setItem(this.productComparisonIdsKey, JSON.stringify(this.productIds));

		if (productName) {
			this.trackRemoveComparisonProduct(productId, productName);
		}

		await this.removeProduct(productId);

		this.updateToggleStates();

		const updatedEvent = Helpers.createCustomEventForDispatch('productComparisonUpdated');
		document.dispatchEvent(updatedEvent);
	}

	private loadProductIds() {
		const storedIds = JSON.parse(localStorage.getItem(this.productComparisonIdsKey) || '[]');
		this.productIds = Array.isArray(storedIds) ? storedIds : [];

		// Only fetch all products on initial load
		if (JSON.stringify(this.productIds) !== JSON.stringify(this.lastProductIds)) {
			this.fetchProductDetails();
			this.lastProductIds = [...this.productIds];
		}
	}

	private updateToggleStates() {
		const productCards = document.querySelectorAll('product-card');
		const isAtMaxLimit = this.productIds.length >= this.maxProducts;

		productCards.forEach((productCard: any) => {
			if (!productCard.product || !this.comparingMode) return;

			const productId = productCard.product.Name;
			const isSelected = this.productIds.includes(productId);

			const toggleInput = productCard.querySelector('.c-toggle__input') as HTMLInputElement;
			const toggleContainer = productCard.querySelector('.c-product__comparison-toggle') as HTMLElement;

			if (!toggleInput || !toggleContainer) return;

			toggleInput.onclick = null;
			toggleContainer.onclick = null;

			if (isAtMaxLimit && !isSelected) {
				toggleInput.disabled = true;
				toggleInput.setAttribute('aria-disabled', 'true');

				toggleContainer.onclick = (e: MouseEvent) => {
					e.preventDefault();
					this.showMaxProductsToast();
					return false;
				};
			} else {
				toggleInput.disabled = false;
				toggleInput.setAttribute('aria-disabled', 'false');
				toggleContainer.onclick = null;
			}

			toggleInput.checked = isSelected;

			if (isSelected) {
				toggleContainer.classList.add('is-selected', 'is-active');
			} else {
				toggleContainer.classList.remove('is-selected', 'is-active');
			}
		});
	}

	async handleUpdateProduct(event: Event) {

		const productToUpdate = (event as CustomEvent).detail.productId;

		if (!productToUpdate) {
			return;
		}

		if (!this.productIds.includes(productToUpdate) && this.productIds.length >= this.maxProducts) {
			this.showMaxProductsToast();
			return;
		}

		// if the product is already in productIDs, we need to remove it
		if (this.productIds.includes(productToUpdate)) {
			const productToRemove = this.products.find(product => product.Product.Id === productToUpdate);
			const productName = productToRemove?.Product.DisplayName;
			this.productIds = this.productIds.filter(id => id !== productToUpdate);
			localStorage.setItem(this.productComparisonIdsKey, JSON.stringify(this.productIds));

			this.trackRemoveComparisonProduct(productToUpdate, productName);

			await this.removeProduct(productToUpdate);
			this.updateToggleStates();
			return;
		}

		// if the product is not in productIDs, we need to add it
		if (!this.productIds.includes(productToUpdate)) {
			this.productIds = [...this.productIds, productToUpdate];
			localStorage.setItem(this.productComparisonIdsKey, JSON.stringify(this.productIds));

			// Fetch product to get name for tracking
			const product = await this.fetchSingleProduct(productToUpdate);
			const productName = product?.Product.DisplayName;

			// Track addition
			this.trackAddComparisonProduct(productToUpdate, productName);

			await this.addProduct(productToUpdate);
			this.updateToggleStates();
		}
	}

	private showMaxProductsToast() {
		let toast = document.querySelector('toast-notification');
		if (!toast) {
			toast = document.createElement('toast-notification');
			document.body.appendChild(toast);
		}
		// @ts-ignore
		toast.show(
			'Comparison Limit Reached',
			`You can only compare up to ${this.maxProducts} products at once.`,
			'warning',
			4000
		);
	}

	private clearAllComparison() {
		this.products.forEach(product => {
			this.trackRemoveComparisonProduct(product.Product.Id, product.Product.DisplayName);
		});
		localStorage.setItem(this.productComparisonIdsKey, JSON.stringify([]));
		const updatedEvent = Helpers.createCustomEventForDispatch('productComparisonUpdated');
		document.dispatchEvent(updatedEvent);
		this.showComparisonBar = false;
		this.productIds = [];
		this.products = [];
		this.updateToggleStates();

		if (this.splideInstance) {
			this.splideInstance.destroy();
			this.splideInstance = null;
		}
	}

	private shouldShowComparisonBar() {
		this.showComparisonBar = this.comparingMode && this.productIds.length > 0;
	}

	private handleComparisonModeChange = (event: CustomEvent) => {
		this.comparingMode = event.detail.mode === "true";
		this.shouldShowComparisonBar();
		this.updateToggleStates();
	}

	private checkIsComparingMode() {
		const comparingMode = localStorage.getItem('comparingMode');
		if (comparingMode === 'true') {
			this.comparingMode = true;
			this.updateToggleStates();
		} else {
			this.comparingMode = false;
		}
		if (this.comparingMode) {
			document.documentElement.classList.add('comparing-mode');
		} else {
			document.documentElement.classList.remove('comparing-mode');
		}
	}

	private getComparisonURL() {
		return `/compare?ids=${this.productIds.join(',')}`;
	}

	private trackAddComparisonProduct(productId: string, productName: string) {
		Tracking.customEvent("add_comparison_product", {
			items : [
				{
					item_id: productId,
					item_name: productName,
				}
			]
		});
	}

	private trackRemoveComparisonProduct(productId: string, productName: string) {
		Tracking.customEvent("remove_comparison_product", {
			items: [
				{
					item_id: productId,
					item_name: productName,
				}
			]
		});
	}

	render() {
		if (!this.shouldShowBarOnPage()) {
			return html``;
		}

		return html`
			<div class="c-product-comparison__bar ${!this.showComparisonBar ? 'is-hidden' : ''}" data-test="comparison-bar">
				<div class="c-product-comparison__bar-inner">
					<div class="c-product-comparison__bar-header">
						<div class="c-product-comparison__info">
							<p class="c-product-comparison__bar-subtitle u-bold" data-test="comparison-bar-product-count">${this.productIds.length} of ${this.maxProducts} product${this.productIds.length !== 1 ? 's' : ''} selected</p>
						</div>
						<div class="c-product-comparison__thumbnails" data-test="comparison-bar-thumbnails">
							${this.validProducts.slice(0, this.maxProducts).map(
							(product) => html`
								<div class="c-product-comparison__thumbnail" data-test="comparison-bar-thumbnail" data-product-id="${product.Product.Id}">
									<a href="${product.Product.Url}" class="c-product-comparison__thumbnail-link" target="_blank">
										<img
											src="${product.Product.Gallery.PrimaryGalleryItem.Url}?height=88&width=88&mode=crop&scale=both&quality=80"
											alt="Product ${product.Product.DisplayName}"
											class="c-product-comparison__thumbnail-image"
											width="88"
											height="88"
										/>
									</a>
									<button
										class="u-link-button c-product-comparison__thumbnail-remove-button"
										data-test="comparison-bar-remove-product-button"
										data-product-id="${product.Product.Id}"
										@click=${() => this.handleRemoveProduct(product.Product.Id)}
									>
										<span class="u-visuallyhidden">Remove</span>
										<img src="/images/icons/svgs/close.svg?v=21045" class="c-icon noPrint" width="14" height="14" loading="lazy" alt="" />
									</button>
								</div>
							`
							)}
						</div>
						<div class="c-product-comparison__bar-buttons">
							<button class="u-link-button c-product-comparison__bar-buttons--clear u-bold" data-test="comparison-bar-clear-button" @click=${this.clearAllComparison.bind(this)}>Clear <span class="u-visuallyhidden">all</span></button>
							${this.productIds.length > 1 ? html`<a href="${this.getComparisonURL()}" class="c-product-comparison__bar-button c-button c-button--on-dark c-product-comparison__bar-buttons--compare" data-test="comparison-bar-compare-button">Compare <span class="c-product-comparison__bar-button-count">(${this.productIds.length} of ${this.maxProducts})</span></a>` : html`<span class="c-product-comparison__bar-button c-button c-button--on-dark c-product-comparison__bar-buttons--compare">Compare <span class="c-product-comparison__bar-button-count">(${this.productIds.length} of ${this.maxProducts})</span></span>`}
						</div>
					</div>
				</div>
			</div>
		`;
	}
}
